import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  SimpleButton,
  LoadingButton,
  TextArea,
  Select,
  Input,
  NotificationsList,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import {
  BsCheckCircle,
  BsClockHistory,
  BsFileEarmarkText,
  BsBoxArrowUpRight,
  BsShieldCheck,
} from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import PppDialog from "./PppDialog";
import { useAuthUser } from "../hooks/useAuthUser";

// ─── Costanti ────────────────────────────────────────────────────────────────

const STATO_DOC_GARA_CFG = {
  in_preparazione: { cls: "bg-yellow-100 text-yellow-800", label: "In preparazione" },
  pronta:          { cls: "bg-blue-100 text-blue-800",     label: "Pronta" },
  esportata:       { cls: "bg-green-100 text-green-800",   label: "Esportata" },
};

const STATO_DOCUMENTO_CFG = {
  da_predisporre: { cls: "bg-gray-100 text-gray-600",    label: "Da predisporre" },
  in_revisione:   { cls: "bg-yellow-100 text-yellow-700", label: "In revisione" },
  approvato:      { cls: "bg-green-100 text-green-800",   label: "Approvato" },
};

const TIPO_DOCUMENTO_LABELS = {
  bando:                    "Bando / Avviso di gara",
  disciplinare:             "Disciplinare di gara",
  pfte_base_gara:           "PFTE (a base di gara)",
  pef_sintesi:              "Sintesi Piano Economico-Finanziario",
  schema_convenzione:       "Schema di Convenzione",
  capitolato_prestazionale: "Capitolato prestazionale",
  altro:                    "Altro documento",
};

const STATO_DOCUMENTO_OPTIONS = [
  { value: "da_predisporre", label: "Da predisporre" },
  { value: "in_revisione",   label: "In revisione" },
  { value: "approvato",      label: "Approvato" },
];

function StatoBadge({ stato, cfg }) {
  const c = cfg[stato];
  if (!c) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${c.cls}`}>
      {c.label}
    </span>
  );
}

// ─── Pannello documento singolo ───────────────────────────────────────────────

function DocumentoRow({ documento, onAggiorna, canGestisci, procedimentoId, propostaId, documentazioneId }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    titolo:                  documento.titolo,
    descrizione:             documento.descrizione ?? "",
    stato:                   documento.stato,
    soggetto_pubblicazione:  documento.soggettoPubblicazione ? "1" : "0",
    contiene_dati_riservati: documento.contieneDatiRiservati ? "1" : "0",
    note:                    documento.note ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState(null);
  const api = usePppApi();
  const { addToast } = useStateContext();

  const handleSalva = () => {
    setSaving(true);
    setErrors(null);
    api.aggiornaDocumentoGara(procedimentoId, propostaId, documentazioneId, documento.id, {
      titolo:                  form.titolo,
      descrizione:             form.descrizione || null,
      stato:                   form.stato,
      soggetto_pubblicazione:  form.soggetto_pubblicazione === "1",
      contiene_dati_riservati: form.contiene_dati_riservati === "1",
      note:                    form.note || null,
    }, {
      onSuccess: () => {
        setSaving(false);
        setEditing(false);
        addToast({ type: "success", message: "Documento aggiornato." });
        onAggiorna();
      },
      onFormError: (body, text) => {
        setSaving(false);
        setErrors([text]);
      },
    });
  };

  return (
    <div className="border rounded-lg bg-white p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 uppercase tracking-wide">
              {TIPO_DOCUMENTO_LABELS[documento.tipo] ?? documento.tipo}
            </span>
            <StatoBadge stato={documento.stato} cfg={STATO_DOCUMENTO_CFG} />
            {documento.soggettoPubblicazione && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-700 font-medium">
                Pubbl. obbligatoria
              </span>
            )}
            {documento.contieneDatiRiservati && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium">
                Dati riservati (art. 35)
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-900 mt-0.5">{documento.titolo}</p>
          {documento.descrizione && (
            <p className="text-xs text-gray-500 mt-0.5">{documento.descrizione}</p>
          )}
        </div>
        {canGestisci && !editing && (
          <SimpleButton text="Modifica" customFunc={() => setEditing(true)} />
        )}
      </div>

      {editing && (
        <div className="border-t pt-3 mt-2 space-y-3">
          {errors && <NotificationsList errors={errors} />}
          <Input label="Titolo" value={form.titolo} onChange={(e) => setForm((f) => ({ ...f, titolo: e.target.value }))} />
          <TextArea label="Descrizione" value={form.descrizione} onChange={(e) => setForm((f) => ({ ...f, descrizione: e.target.value }))} rows={2} />
          <Select
            label="Stato"
            value={form.stato}
            options={STATO_DOCUMENTO_OPTIONS}
            onChange={(e) => setForm((f) => ({ ...f, stato: e.target.value }))}
          />
          <Select
            label="Soggetto a pubblicazione (art. 28)"
            value={form.soggetto_pubblicazione}
            options={[{ value: "1", label: "Sì" }, { value: "0", label: "No" }]}
            onChange={(e) => setForm((f) => ({ ...f, soggetto_pubblicazione: e.target.value }))}
          />
          <Select
            label="Contiene dati riservati (art. 35)"
            value={form.contiene_dati_riservati}
            options={[{ value: "0", label: "No" }, { value: "1", label: "Sì" }]}
            onChange={(e) => setForm((f) => ({ ...f, contiene_dati_riservati: e.target.value }))}
          />
          <TextArea label="Note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={2} />
          <div className="flex gap-2 justify-end">
            <SimpleButton text="Annulla" customFunc={() => { setEditing(false); setErrors(null); }} />
            <LoadingButton isLoading={saving} text="Salva" customFunc={handleSalva} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Componente principale ────────────────────────────────────────────────────

export default function Fase14Tab({ proposta, procedimentoId, onPropostaReload }) {
  const api = usePppApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const { addToast } = useStateContext();
  const { can } = useAuthUser();

  const canView      = can("ppp.documentazione_gara.view");
  const canCrea      = can("ppp.documentazione_gara.create");
  const canGestisci  = can("ppp.documenti_gara.manage");
  const canFinalizza = can("ppp.documentazione_gara.finalize");
  const canEsporta   = can("ppp.documentazione_gara.export");

  const [documentazione, setDocumentazione] = useState(null);
  const [loading, setLoading] = useState(false);

  // Avvio fase
  const [avvioOpen, setAvvioOpen] = useState(false);
  const [avvioForm, setAvvioForm] = useState({
    criterio_aggiudicazione: "oepv",
    peso_qualita: "70",
    peso_prezzo: "30",
    note: "",
  });
  const [avvioSaving, setAvvioSaving] = useState(false);
  const [avvioErrors, setAvvioErrors] = useState(null);

  // Finalizzazione / Esportazione
  const [finalizzaSaving, setFinalizzaSaving] = useState(false);
  const [esportaSaving, setEsportaSaving] = useState(false);
  const [azioneErrors, setAzioneErrors] = useState(null);

  const loadDocumentazione = useCallback(() => {
    if (!proposta?.documentazioneGaraId) {
      setDocumentazione(null);
      return;
    }
    setLoading(true);
    apiRef.current.getDocumentazioneGara(procedimentoId, proposta.id, proposta.documentazioneGaraId, {
      onSuccess: (data) => {
        setLoading(false);
        setDocumentazione(data);
      },
      onFormError: () => setLoading(false),
    });
  }, [procedimentoId, proposta?.id, proposta?.documentazioneGaraId]);

  useEffect(() => {
    loadDocumentazione();
  }, [loadDocumentazione]);

  const handleAvvia = () => {
    setAvvioSaving(true);
    setAvvioErrors(null);
    apiRef.current.avviaDocumentazioneGara(procedimentoId, proposta.id, {
      criterio_aggiudicazione: avvioForm.criterio_aggiudicazione,
      peso_qualita:            avvioForm.peso_qualita ? parseInt(avvioForm.peso_qualita) : null,
      peso_prezzo:             avvioForm.peso_prezzo ? parseInt(avvioForm.peso_prezzo) : null,
      note:                    avvioForm.note || null,
    }, {
      onSuccess: () => {
        setAvvioSaving(false);
        setAvvioOpen(false);
        addToast({ type: "success", message: "Fase 14 avviata. Struttura documentale creata." });
        onPropostaReload?.();
      },
      onFormError: (body, text) => {
        setAvvioSaving(false);
        if ((body?.message ?? "").includes("Esiste gi")) {
          setAvvioOpen(false);
          onPropostaReload?.();
        } else {
          setAvvioErrors([text]);
        }
      },
    });
  };

  const handleFinalizza = () => {
    if (!window.confirm("Finalizzare il pacchetto documentale? Tutti i documenti devono essere in stato 'Approvato'.")) return;
    setFinalizzaSaving(true);
    setAzioneErrors(null);
    apiRef.current.finalizzaDocumentazioneGara(procedimentoId, proposta.id, documentazione.id, {
      onSuccess: () => {
        setFinalizzaSaving(false);
        addToast({ type: "success", message: "Documentazione di gara finalizzata." });
        loadDocumentazione();
      },
      onFormError: (body, text) => {
        setFinalizzaSaving(false);
        setAzioneErrors([text]);
      },
    });
  };

  const handleEsporta = () => {
    if (!window.confirm("Esportare il pacchetto verso la piattaforma di approvvigionamento digitale? Questa azione segna la fase come completata.")) return;
    setEsportaSaving(true);
    setAzioneErrors(null);
    apiRef.current.esportaDocumentazioneGara(procedimentoId, proposta.id, documentazione.id, {
      onSuccess: () => {
        setEsportaSaving(false);
        addToast({ type: "success", message: "Pacchetto esportato. Fase 14 completata." });
        loadDocumentazione();
      },
      onFormError: (body, text) => {
        setEsportaSaving(false);
        setAzioneErrors([text]);
      },
    });
  };

  if (!canView) return null;

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const integrazionePfteApprovata = proposta?.integrazionePfteApprovata === true;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-800">
            Fase 14 — Predisposizione Documentazione di Gara
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Art. 193, comma 8, D.lgs 36/2023 — OEPV — Art. 25 (piattaforma digitale)
          </p>
        </div>
        {documentazione && (
          <StatoBadge stato={documentazione.stato} cfg={STATO_DOC_GARA_CFG} />
        )}
      </div>

      {/* Avvio fase — nessuna documentazione creata */}
      {!documentazione && !proposta?.documentazioneGaraId && integrazionePfteApprovata && (
        <div className="border border-dashed border-blue-300 rounded-lg bg-blue-50 p-6 text-center space-y-3">
          <BsFileEarmarkText className="mx-auto text-blue-400 text-3xl" />
          <p className="text-sm text-blue-800 font-medium">
            La proposta è ammessa e il PFTE è approvato. Avvia la Fase 14 per predisporre il pacchetto documentale di gara.
          </p>
          <p className="text-xs text-blue-600">
            Verranno creati automaticamente i 6 documenti obbligatori (art. 193, co. 8): bando, disciplinare, PFTE, PEF, schema di convenzione, capitolato prestazionale.
          </p>
          {canCrea && (
            <div className="flex justify-center">
              <SimpleButton text="Avvia Fase 14 — Documentazione di Gara" customFunc={() => setAvvioOpen(true)} />
            </div>
          )}
        </div>
      )}

      {!documentazione && !proposta?.documentazioneGaraId && !integrazionePfteApprovata && (
        <div className="text-center py-10 text-gray-400 text-sm">
          <BsClockHistory className="mx-auto mb-2 text-2xl" />
          La Fase 14 si attiva dopo il completamento della Fase 13 (Approvazione PFTE).
        </div>
      )}

      {/* Contenuto principale */}
      {documentazione && (
        <>
          {/* Errori azioni */}
          {azioneErrors && <NotificationsList errors={azioneErrors} />}

          {/* Barra azioni */}
          {documentazione.stato !== "esportata" && (
            <div className="flex flex-wrap gap-2 justify-end">
              {documentazione.stato === "in_preparazione" && canFinalizza && (
                <LoadingButton
                  isLoading={finalizzaSaving}
                  text="Finalizza pacchetto"
                  customFunc={handleFinalizza}
                />
              )}
              {documentazione.stato === "pronta" && canEsporta && (
                <LoadingButton
                  isLoading={esportaSaving}
                  text="Esporta verso piattaforma digitale (art. 25)"
                  customFunc={handleEsporta}
                />
              )}
            </div>
          )}

          {documentazione.stato === "esportata" && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-center gap-3">
              <BsBoxArrowUpRight className="text-green-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-800">
                  Pacchetto esportato verso la piattaforma di approvvigionamento digitale
                </p>
                <p className="text-xs text-green-600">
                  {documentazione.esportataIl ? new Date(documentazione.esportataIl).toLocaleString("it-IT") : ""}
                  {" — "}La procedura di gara prosegue esternamente a Qualifia.
                </p>
              </div>
            </div>
          )}

          {/* Configurazione criterio aggiudicazione */}
          <div className="border rounded-lg bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <BsShieldCheck className="text-purple-600" />
              <h4 className="text-sm font-semibold text-gray-800">
                Criterio di aggiudicazione — OEPV (art. 108 D.lgs 36/2023)
              </h4>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide block">Criterio</span>
                <span className="font-medium">Offerta Economicamente Più Vantaggiosa</span>
              </div>
              {documentazione.pesoQualita != null && (
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block">Peso qualità</span>
                  <span>{documentazione.pesoQualita}%</span>
                </div>
              )}
              {documentazione.pesoPrezzo != null && (
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block">Peso prezzo</span>
                  <span>{documentazione.pesoPrezzo}%</span>
                </div>
              )}
            </div>
            {documentazione.note && (
              <p className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{documentazione.note}</p>
            )}
          </div>

          {/* Documenti di gara */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <BsFileEarmarkText className="text-blue-500" />
              Documenti del pacchetto di gara
              <span className="text-xs font-normal text-gray-400">
                ({documentazione.documenti?.filter((d) => d.stato === "approvato").length ?? 0}/{documentazione.documenti?.length ?? 0} approvati)
              </span>
            </h4>
            <div className="space-y-2">
              {(documentazione.documenti ?? []).map((doc) => (
                <DocumentoRow
                  key={doc.id}
                  documento={doc}
                  onAggiorna={loadDocumentazione}
                  canGestisci={canGestisci && documentazione.stato !== "esportata"}
                  procedimentoId={procedimentoId}
                  propostaId={proposta.id}
                  documentazioneId={documentazione.id}
                />
              ))}
            </div>
          </div>

          {documentazione.finalizzataIl && (
            <p className="text-xs text-gray-400 text-right">
              Finalizzato il {new Date(documentazione.finalizzataIl).toLocaleString("it-IT")}
            </p>
          )}
        </>
      )}

      {/* Dialog avvio fase 14 */}
      <PppDialog
        open={avvioOpen}
        onClose={() => { setAvvioOpen(false); setAvvioErrors(null); }}
        title="Avvia Fase 14 — Predisposizione Documentazione di Gara"
        footer={
          <div className="flex gap-2 justify-end">
            <SimpleButton text="Annulla" customFunc={() => { setAvvioOpen(false); setAvvioErrors(null); }} />
            <LoadingButton isLoading={avvioSaving} text="Avvia Fase 14" customFunc={handleAvvia} />
          </div>
        }
      >
        <div className="space-y-4">
          {avvioErrors && <NotificationsList errors={avvioErrors} />}
          <p className="text-sm text-gray-600">
            Verranno creati automaticamente i documenti obbligatori (art. 193, co. 8).
            Il criterio di aggiudicazione è <strong>OEPV</strong> (art. 108 D.lgs 36/2023).
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Peso qualità (%)"
              type="number"
              min="0"
              max="100"
              value={avvioForm.peso_qualita}
              onChange={(e) => setAvvioForm((f) => ({ ...f, peso_qualita: e.target.value }))}
            />
            <Input
              label="Peso prezzo (%)"
              type="number"
              min="0"
              max="100"
              value={avvioForm.peso_prezzo}
              onChange={(e) => setAvvioForm((f) => ({ ...f, peso_prezzo: e.target.value }))}
            />
          </div>
          <TextArea
            label="Note"
            value={avvioForm.note}
            onChange={(e) => setAvvioForm((f) => ({ ...f, note: e.target.value }))}
            rows={3}
          />
        </div>
      </PppDialog>
    </div>
  );
}
